import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../lib/api';
import { toast } from '@vatman/ui';
import { errorText } from '../i18n';
import { useEngine } from '../board/useEngine';
import { templatesApi, type CustomTemplate } from './api';
import { placeTemplate } from './board';
import { TeamTemplatesGallery } from './Gallery';

export type TemplateTab = 'builtin' | 'team';

const TAB_KEY = 'vatman.templatesTab';

/** Built-in vs. team templates; the choice is remembered between the dashboard and boards. */
export function useTemplateTab(): [TemplateTab, (tab: TemplateTab) => void] {
  const [tab, setTab] = useState<TemplateTab>(() => {
    try {
      return localStorage.getItem(TAB_KEY) === 'team' ? 'team' : 'builtin';
    } catch {
      return 'builtin';
    }
  });
  return [
    tab,
    (next) => {
      setTab(next);
      try {
        localStorage.setItem(TAB_KEY, next);
      } catch {
        // Remembered for this page only.
      }
    },
  ];
}

export function TemplateTabs({ tab, onChange, className }: { tab: TemplateTab; onChange: (tab: TemplateTab) => void; className?: string }) {
  const { t } = useTranslation();
  return (
    <div className={`segmented ${className ?? ''}`} role="tablist">
      <button type="button" role="tab" aria-selected={tab === 'builtin'} data-active={tab === 'builtin'} onClick={() => onChange('builtin')}>
        {t('teamTemplates.builtIn')}
      </button>
      <button type="button" role="tab" aria-selected={tab === 'team'} data-active={tab === 'team'} onClick={() => onChange('team')}>
        {t('teamTemplates.tab')}
      </button>
    </div>
  );
}

/** Team templates inside the board's templates popover: a click inserts one at the viewport center. */
export function BoardTeamTemplates({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  const e = useEngine();
  const qc = useQueryClient();
  const [busyId, setBusyId] = useState<string | null>(null);

  const use = async (tpl: CustomTemplate) => {
    if (busyId) return;
    setBusyId(tpl.id);
    try {
      // Keyed by the edit time, so a renamed or replaced template is fetched again.
      const full = await qc.fetchQuery({ queryKey: ['template', tpl.id, tpl.updatedAt], queryFn: () => templatesApi.get(tpl.id), staleTime: Infinity });
      placeTemplate(e, full.items);
      onDone();
    } catch (err) {
      toast(errorText(err instanceof ApiError ? err.code : 'internal'));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div aria-label={t('teamTemplates.tab')}>
      <TeamTemplatesGallery compact busyId={busyId} onUse={(tpl) => void use(tpl)} />
    </div>
  );
}
